import { NearestFilter, ShaderMaterial, Texture, Vector3 } from 'three';

import fragmentShader from '../../../../../../../static/glsl/cesium/cesium-points.frag';
import vertexShader from '../../../../../../../static/glsl/cesium/cesium-points.vert';
import { TextureManager } from '../../../../../utilities/texture-manager/texture-manager';

export class CesiumPointsMaterial {
  static get instance() {
    return CesiumPointsMaterial._instance;
  }

  get displayClasses(): boolean {
    return this.shaderMaterial.uniforms.displayClasses.value;
  }

  set displayClasses(state: boolean) {
    this.shaderMaterial.uniforms.displayClasses.value = state;
  }

  get attenuation(): boolean {
    return this.shaderMaterial.uniforms.attenuation.value;
  }

  set attenuation(state: boolean) {
    this.shaderMaterial.uniforms.attenuation.value = state;
  }

  get pointScale(): number {
    return this.shaderMaterial.uniforms.pointScale.value;
  }

  set pointScale(state: number) {
    this.shaderMaterial.uniforms.pointScale.value = state;
  }

  set lightDirection(state: Vector3) {
    this.shaderMaterial.uniforms.lightDirection.value = state;
  }

  set applyShading(state: boolean) {
    this.shaderMaterial.uniforms.applyShading.value = state;
  }

  set opacity(opacity: number) {
    this.shaderMaterial.uniforms.opacity.value = opacity;
  }

  /** The drawing buffer's height in pixels, used to size points for an orthographic camera. */
  set screenHeight(height: number) {
    this.shaderMaterial.uniforms.screenHeight.value = height;
  }

  get material() {
    return this.shaderMaterial;
  }
  private static _instance: CesiumPointsMaterial = new CesiumPointsMaterial();
  imageData?: ImageData;
  private shaderMaterial: ShaderMaterial;

  private constructor() {
    this.shaderMaterial = this.createShaderMaterial();
  }

  /**
   *  Loads the points classification color map, and attaches it to the shader texture uniform.
   */
  async initialize() {
    this.displayClasses = false;
    this.imageData = undefined;

    if (this.shaderMaterial) {
      this.shaderMaterial.dispose();
    }

    this.shaderMaterial = this.createShaderMaterial();
    const texture = await TextureManager.instance.loadSync(
      '/static/assets/textures/point-cloud-classification-colors.png',
    );
    this.setTexture(texture);
  }

  setTexture(texture: Texture) {
    texture.magFilter = NearestFilter;
    texture.minFilter = NearestFilter;
    this.material.uniforms.texture1.value = texture;
    texture.needsUpdate = true;
  }

  isClassificationVisible(classIndex: number) {
    if (!this.imageData) {
      return true;
    }

    return this.imageData.data[classIndex * 4 + 3] !== 0;
  }

  toggleClassVisibility(classIndex: number) {
    if (!this.imageData) {
      return;
    }

    this.imageData.data[classIndex * 4 + 3] = this.isClassificationVisible(classIndex) ? 0 : 255;
  }

  private createShaderMaterial() {
    return new ShaderMaterial({
      vertexShader,
      fragmentShader,
      uniforms: {
        texture1: { value: undefined },
        displayClasses: { value: false },
        applyShading: { value: true },
        attenuation: { value: true },
        pointScale: { value: 1.0 },
        screenHeight: { value: 1080 },
        opacity: { value: 1.0 },
        lightDirection: { value: new Vector3(0.5, 0.5, 1.0).normalize() },
      },
      clipping: true,
      vertexColors: true,
      transparent: true,
    });
  }
}
